import { auth } from './firebase.js';
import { signInAnonymously } from 'firebase/auth';

const CLOUD_RUN_URL = "https://arberia-ai-orchestrator-326957404910.europe-west1.run.app";
const LOCAL_DEV_URL = "http://localhost:8080";

function getEndpoints() {
    const isLocal = typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    
    if (isLocal) {
        return [
            `${LOCAL_DEV_URL}/api/chat`,
            `${CLOUD_RUN_URL}/api/chat`
        ];
    }
    return [`${CLOUD_RUN_URL}/api/chat`];
}

/**
 * Fallback locale per la guida culturale offline
 */
function getLocalFallbackAnswer(message) {
    const m = (message || '').toLowerCase();
    if (m.includes('lungro') || m.includes('ungra') || m.includes('sale') || m.includes('salina')) {
        return {
            answer: "A Lungro (Ungra) puoi ammirare la magnifica Cattedrale bizantina di San Nicola di Mira e la storica Miniera di Salgemma, l'oro bianco che ha dato lavoro a generazioni di arbëreshë.",
            reply: "A Lungro (Ungra) puoi ammirare la magnifica Cattedrale bizantina di San Nicola di Mira e la storica Miniera di Salgemma, l'oro bianco che ha dato lavoro a generazioni di arbëreshë.",
            actions: [{ type: 'SHOW_POI', payload: { poiId: 'lungro-salina' } }],
            sources: ["Guida Locale Arberia"]
        };
    }
    if (m.includes('civita') || m.includes('diavolo') || m.includes('raganello')) {
        return {
            answer: "A Civita (Çifti) ti aspettano le suggestive Case Kodra con le facciate antropomorfe e il celebre Ponte del Diavolo che sovrasta le mozzafiato Gole del Raganello.",
            reply: "A Civita (Çifti) ti aspettano le suggestive Case Kodra con le facciate antropomorfe e il celebre Ponte del Diavolo che sovrasta le mozzafiato Gole del Raganello.",
            actions: [{ type: 'SHOW_POI', payload: { poiId: 'civita-ponte' } }],
            sources: ["Guida Locale Arberia"]
        };
    }
    if (m.includes('skanderbeg') || m.includes('storia')) {
        return {
            answer: "Giorgio Castriota Skanderbeg è il leggendario condottiero albanese del XV secolo. La sua memoria è celebrata in ogni comunità arbëreshë con canti, statue e tradizioni secolari.",
            reply: "Giorgio Castriota Skanderbeg è il leggendario condottiero albanese del XV secolo. La sua memoria è celebrata in ogni comunità arbëreshë con canti, statue e tradizioni secolari.",
            actions: [],
            sources: ["Guida Locale Arberia"]
        };
    }
    return {
        answer: "Mirëdita! Sono ArberIA, la tua guida tra i borghi e la cultura d'Arbëria. Chiedimi informazioni su Lungro, Civita, Firmo, i costumi d'oro, la vallja o le ricette tradizionali!",
        reply: "Mirëdita! Sono ArberIA, la tua guida tra i borghi e la cultura d'Arbëria. Chiedimi informazioni su Lungro, Civita, Firmo, i costumi d'oro, la vallja o le ricette tradizionali!",
        actions: [],
        sources: ["Guida Locale Arberia"]
    };
}

export const AIService = {
    /**
     * Invia un messaggio all'Orchestratore AI (con failover trasparente e auth guest)
     * @param {string} message - Testo utente
     * @param {object} gps - { lat, lng, accuracy } (opzionale)
     * @param {string} sessionId - ID sessione chat (opzionale)
     * @returns {Promise<object>} Risposta AI { answer, reply, actions, sources }
     */
    async sendMessage(message, gps = null, sessionId = null) {
        if (!navigator.onLine) {
            return getLocalFallbackAnswer(message);
        }

        // Recupero o generazione token autenticato (supporta utente loggato e guest anonimo)
        let token = 'guest-pwa-token';
        try {
            let user = auth.currentUser;
            if (!user && typeof signInAnonymously === 'function') {
                try {
                    const cred = await signInAnonymously(auth);
                    user = cred.user;
                } catch {
                    // Fallback to guest token
                }
            }
            if (user) {
                token = await user.getIdToken(false);
            }
        } catch {
            token = 'guest-pwa-token';
        }

        const payload = {
            message: message,
            channel: "PWA",
            sessionId: sessionId || "session-" + Date.now()
        };

        if (gps && typeof gps.lat === 'number' && typeof gps.lng === 'number') {
            payload.gps = gps;
            payload.location = { lat: gps.lat, lng: gps.lng };
        }

        const endpoints = getEndpoints();

        for (const endpoint of endpoints) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 12000); // 12s per endpoint

                const response = await fetch(endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                        "x-guest-session": sessionId || "guest-session"
                    },
                    body: JSON.stringify(payload),
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (response.ok) {
                    const data = await response.json();
                    
                    const answerText = data.answer || data.reply || "Risposta culturale elaborata.";
                    const actionsList = data.actions || (data.action && data.action !== 'NONE' ? [{
                        type: data.action === 'SUGGEST_POI' ? 'SHOW_POI' : data.action,
                        payload: data.actionData || {}
                    }] : []);

                    return {
                        answer: answerText,
                        reply: answerText,
                        action: data.action || 'NONE',
                        actions: actionsList,
                        actionData: data.actionData,
                        sources: data.sources || ["ArberiaExperience Archive"]
                    };
                }
            } catch (err) {
                // Prova il prossimo endpoint
            }
        }

        // Se nessun server remoto ha risposto entro i timeout, usa la knowledge base locale di emergenza
        return getLocalFallbackAnswer(message);
    }
};
